import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import * as styles from './DocumentPopover.module.css';

const DocumentPopover = ({ trigger, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleToggle = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX + rect.width / 2
      });
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target)
      ) {
        handleClose();
      }
    };

    const handleScroll = () => {
      handleClose();
    };

    // Small delay to prevent immediate closing when opening
    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }, 0);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, handleClose]);

  return (
    <>
      <div ref={containerRef} className={styles.container}>
        <div ref={triggerRef} className={styles.trigger} onClick={handleToggle}>
          {trigger}
        </div>
      </div>
      {isOpen && createPortal(
        <div
          ref={popoverRef}
          className={styles.popover}
          style={{
            top: position.top,
            left: position.left,
            position: 'absolute'
          }}
        >
          <div className={styles.content}>{children(handleClose)}</div>
        </div>,
        document.body
      )}
    </>
  );
};

export default DocumentPopover;
